import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
} from '@mui/material';
import {
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  NoDataSkeleton,
  ActionIconButton,
} from '../../../../common/components';
import {
  getAllotmentLotLabel,
  splitCellText,
  isQualityCommentCompleted,
} from '../utils/qualityCommentsUtils';

const QualityCommentsDataTable = ({
  data,
  isLoading,
  onEdit,
  onToggleCompleted,
  LoadingComponent,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const formatDateTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString('es-AR');
  };

  if (isMobile) {
    return (
      <GeneralTableBox>
        {isLoading ? (
          <LoadingComponent />
        ) : data && data.length > 0 ? (
          <Box sx={{ p: 2 }}>
            {data.map((comment, index) => (
              <Box
                key={comment.uuid || index}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  p: 2,
                  mb: 2,
                  backgroundColor: 'white',
                }}
              >
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1, mb: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Loteo / Lote
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {getAllotmentLotLabel(comment)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Tarea
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {(() => {
                        const lines = splitCellText(comment.task_name || '-');
                        return lines.map((line, idx) => (
                          <span key={`task-${comment.uuid || idx}-${idx}`}>
                            {line}
                            {idx < lines.length - 1 && <br />}
                          </span>
                        ));
                      })()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Estado
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      color={isQualityCommentCompleted(comment) ? 'success.main' : 'warning.main'}
                    >
                      {isQualityCommentCompleted(comment) ? 'Completado' : 'No completado'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Actualizado
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDateTime(comment.updated_at)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Comentario
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {(() => {
                        const lines = splitCellText(comment.observations || '-');
                        return lines.map((line, idx) => (
                          <span key={`obs-${comment.uuid || idx}-${idx}`}>
                            {line}
                            {idx < lines.length - 1 && <br />}
                          </span>
                        ));
                      })()}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <ActionIconButton variant="edit" onClick={() => onEdit(comment)} />
                  <ActionIconButton
                    variant={isQualityCommentCompleted(comment) ? 'check' : 'close'}
                    onClick={() => onToggleCompleted(comment)}
                    disabled={isQualityCommentCompleted(comment)}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <NoDataSkeleton colSpan={1} message="No se encontraron comentarios" />
        )}
      </GeneralTableBox>
    );
  }

  return (
    <GeneralTableBox>
      <Table aria-label="quality comments table">
        <TableHead>
          <TableRow>
            <HeadTableCell wmd="20%" wxs="30%">
              Loteo / Lote
            </HeadTableCell>
            <HeadTableCell wmd="20%" wxs="25%">
              Tarea
            </HeadTableCell>
            <HeadTableCell wmd="25%" wxs="30%">
              Comentario
            </HeadTableCell>
            <HeadTableCell wmd="15%" wxs="15%">
              Actualizado
            </HeadTableCell>
            <HeadTableCell wmd="10%" wxs="10%">
              Estado
            </HeadTableCell>
            <HeadTableCell wmd="10%" wxs="10%">
              Acciones
            </HeadTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <LoadingComponent />
          ) : data && data.length > 0 ? (
            data.map((comment, index) => (
              <TableRow key={comment.uuid || index}>
                <RowTableCell>{getAllotmentLotLabel(comment)}</RowTableCell>
                <RowTableCell>
                  {(() => {
                    const lines = splitCellText(comment.task_name || '-');
                    return lines.map((line, idx) => (
                      <span key={`task-desktop-${comment.uuid || idx}-${idx}`}>
                        {line}
                        {idx < lines.length - 1 && <br />}
                      </span>
                    ));
                  })()}
                </RowTableCell>
                <RowTableCell>
                  {(() => {
                    const lines = splitCellText(comment.observations || '-');
                    return lines.map((line, idx) => (
                      <span key={`obs-desktop-${comment.uuid || idx}-${idx}`}>
                        {line}
                        {idx < lines.length - 1 && <br />}
                      </span>
                    ));
                  })()}
                </RowTableCell>
                <RowTableCell>{formatDateTime(comment.updated_at)}</RowTableCell>
                <RowTableCell
                  sx={{ color: isQualityCommentCompleted(comment) ? 'success.main' : 'warning.main' }}
                >
                  {isQualityCommentCompleted(comment) ? 'Completado' : 'No completado'}
                </RowTableCell>
                <RowTableCell sx={{ textAlign: 'center' }}>
                  <ActionIconButton variant="edit" onClick={() => onEdit(comment)} />
                  <ActionIconButton
                    variant={isQualityCommentCompleted(comment) ? 'check' : 'close'}
                    onClick={() => onToggleCompleted(comment)}
                    disabled={isQualityCommentCompleted(comment)}
                  />
                </RowTableCell>
              </TableRow>
            ))
          ) : (
            <NoDataSkeleton colSpan={6} message="No se encontraron comentarios" />
          )}
        </TableBody>
      </Table>
    </GeneralTableBox>
  );
};

export default QualityCommentsDataTable;
